import React, { useState, useEffect, useMemo } from 'react'
import { Switch } from '../components/ui/switch'
import { BreadcrumbProps } from '@blueprintjs/core'
import { HTMLSelect } from '../components/ui/html-select'
import { Card } from '../components/ui/card'
import { money, moneyWithCurrencyCode } from '../util/formatters'
import { axiosGet } from '../util/api'
import { useUser } from '../hooks/useUser'
import { Layout } from '../components/Layout'
import {
    LoadFunc,
    useTableHooks,
    useTableHooksFromStore,
} from '../packages/blueprint-components/src/useTableHooks'
import classNames from 'classnames'
import { PerformanceFilters } from '../config'
import ReactCountryFlag from 'react-country-flag'
import { Marketplace, Marketplaces } from '../util/marketplaces'
import { PieChart, PieChartDataGroup, PieChartLegend } from '../components/pie-chart'
import { observer } from 'mobx-react-lite'
import { useLocation } from 'react-router-dom'
import { TableProps } from '../components/ad_groups/AdGroupsTable'
import { Profile } from '../types/profile'
import {
    CellProps,
    defaultPerfColumns,
    ExpandedPerformanceMetrics,
    extra,
} from '../components/table2/ad_grid_columns'
import { useKdpFilters } from '../hooks/useKdpFilters'
import { AgGridColumnDef, AgGridTable, DummyHeader } from '../components/table2/AgGridTable'
import { addExtraCols } from '../components/campaigns/CampaignsTable'
import { userStore } from '../stores/user_store'
import { TableStore } from '../components/table2/table_store'
import { useSavedFiltersStore } from '../hooks/useSavedFiltersStore'
import { isNavV2Enabled } from '../util/nav-version'
import { profileTitle } from '../components/navbar/ProfileSelect'

/**
 * Performs the client side search, sort, and filtering
 */
const mutateResult = (params: any, r: any, showTableInBaseCurrency: boolean) => {
    r.data.totals.profile_id = r.data.totals.name

    const base = showTableInBaseCurrency ? 'converted' : 'current'

    if (params.query) {
        r.data.data = r.data.data.filter((i: any) => {
            const name = profileTitle(i.profile)
            return name.toLowerCase().indexOf(params.query.toLowerCase()) !== -1
        })
    }

    if (params.filter) {
        r.data.data = r.data.data.filter((i: any) => {
            let match = true
            Object.keys(params.filter).forEach((col) => {
                Object.keys(params.filter[col]).forEach((op) => {
                    switch (op) {
                        case '>':
                            if (match) {
                                match = i[base][col] > params.filter[col][op]
                            }
                            break
                        case '<':
                            if (match) {
                                match = i[base][col] < params.filter[col][op]
                            }
                            break
                        case '>=':
                            if (match) {
                                match = i[base][col] >= params.filter[col][op]
                            }
                            break
                        case '<=':
                            if (match) {
                                match = i[base][col] <= params.filter[col][op]
                            }
                            break
                        case '=':
                            if (match) {
                                match = i[base][col] = params.filter[col][op]
                            }
                            break
                        case '!=':
                            if (match) {
                                match = i[base][col] != params.filter[col][op]
                            }
                            break
                        case 'isNull':
                            if (match) {
                                match = i[base][col] === null
                            }
                            break
                        default:
                            console.error('Unhandled filter op:', op)
                    }
                })
            })

            return match
        })
    }

    if (params.sort) {
        const dir = params.sort.substring(0, 1) === '-' ? 'desc' : 'asc'
        const col = dir === 'desc' ? params.sort.substring(1) : params.sort
        r.data.data = r.data.data.sort((a: any, b: any) => {
            if (dir === 'desc') {
                if (a[base][col] == b[base][col]) {
                    return 0
                }
                return a[base][col] < b[base][col] ? 1 : -1
            } else {
                if (a[base][col] == b[base][col]) {
                    return 0
                }
                return a[base][col] > b[base][col] ? 1 : -1
            }
        })
    }

    return r
}

type TableType = ExpandedPerformanceMetrics & { profile: Profile } & PieChartDataGroup
const extractPerformanceData = (
    showTableInBaseCurrency: boolean,
    data: TableType[],
): TableType[] => {
    // Pull out the values so they are on the root object, so
    // the columns can read off of them
    const base = showTableInBaseCurrency ? 'converted' : 'current'
    return data.map((item: any) => {
        return {
            ...item,
            marketplace: Marketplaces[item.profile.marketplace_id as Marketplace],
            cpc: item[base].cpc,
            rpc: item[base].rpc,
            blended_rpc: item[base].blended_rpc,
            cost: item[base].cost,
            sales: item[base].sales,
            adjusted_sales: item[base].adjusted_sales,
            blended_profit: item[base].blended_profit,
            acos: item[base].acos,
            blended_acos: item[base].blended_acos,
            roas: item[base].roas,
            blended_roas: item[base].blended_roas,
            impressions: item[base].impressions,
            clicks: item[base].clicks,
            ctr: item[base].ctr,
            orders: item[base].orders,
            adjusted_orders: item[base].adjusted_orders,
            cvr: item[base].cvr,
            blended_cvr: item[base].blended_cvr,
            cac: item[base].cac,
            blended_cac: item[base].blended_cac,
            aov: item[base].aov,
            blended_aov: item[base].blended_aov,
            pages_read: item[base].pages_read,
            adjusted_pages_read: item[base].adjusted_pages_read,
            estimated_royalties: item[base].estimated_royalties,
            adjusted_estimated_royalties: item[base].adjusted_estimated_royalties,
        }
    })
}

export const DashboardProfiles = observer(({}) => {
    const [baseCurrency, setBaseCurrency] = useState('USD')
    const [showTableInBaseCurrency, setShowTableInBaseCurrency] = useState(false)
    // MER-2802: keyed by profile_id, which may be a number or a string.
    const [visible, setVisible] = useState<{ [key: string]: boolean }>({})
    const [store] = useState(new TableStore<TableType>(userStore, 'ad_accounts_overview'))
    const savedFiltersStore = useSavedFiltersStore()
    const { search } = useLocation()
    const useNavV2 = useMemo(() => isNavV2Enabled(search), [search])

    const load: LoadFunc<TableType> = (params) => {
        return axiosGet(
            'api/v2/profiles-overview',
            {
                ...params,
                base_currency: baseCurrency,
            },
            true,
        ).then((r) => {
            r = mutateResult(params, r, showTableInBaseCurrency)

            const v: { [key: number]: boolean } = {}
            r.data.data
                .filter((i: any) => i.converted.cost > 0)
                .forEach((i: any) => (v[i.profile.profile_id] = true))
            setVisible(v)

            r.data.data = extractPerformanceData(showTableInBaseCurrency, r.data.data)

            return r
        })
    }
    const tableHooks = useTableHooks(load, { perPage: 10 }, true)
    const tableHooks2 = useTableHooksFromStore(load, {}, false, store, undefined, {
        requireProfile: false,
    })

    useEffect(() => {
        tableHooks2.reload()
    }, [baseCurrency])

    useEffect(() => {
        tableHooks2.mutateItems((items) => {
            return extractPerformanceData(showTableInBaseCurrency, items)
        })
    }, [showTableInBaseCurrency])

    const breadcrumbs: BreadcrumbProps[] = [{ text: 'Overview' }]

    return (
        <Layout
            breadcrumbs={breadcrumbs}
            primary="All accounts"
            className={useNavV2 ? 'bg-white' : undefined}
            layoutClassName={useNavV2 ? '!p-0 md:!p-0 bg-white' : undefined}
        >
            <Card
                className={classNames(
                    useNavV2
                        ? 'p-0 mb-0 !rounded-none !shadow-none border-b border-[#E5E7EB]'
                        : 'pt-0 pb-0 mb-6',
                )}
            >
                <div className={classNames('flex', useNavV2 ? 'p-4' : 'pb-4')}>
                    <div style={{ maxWidth: '250px' }} className="flex flex-col items-center mr-8">
                        <div className="text-lg font-normal mb-2">Sales</div>
                        <PieChart
                            data={tableHooks2.items}
                            dataKey="sales"
                            label="Sales"
                            visible={visible}
                        />
                    </div>
                    <div style={{ maxWidth: '250px' }} className="flex flex-col items-center mr-8">
                        <div className="text-lg font-normal mb-2">Spend</div>
                        <PieChart
                            data={tableHooks2.items}
                            dataKey="cost"
                            label="Cost"
                            visible={visible}
                        />
                    </div>
                    <div style={{ maxWidth: '250px' }} className="flex flex-col items-center mr-8">
                        <div className="text-lg font-normal mb-2">Orders</div>
                        <PieChart
                            data={tableHooks2.items}
                            dataKey="orders"
                            label="Orders"
                            visible={visible}
                        />
                    </div>
                    <PieChartLegend
                        visible={visible}
                        onClick={(profileId) =>
                            setVisible((p) => ({
                                ...p,
                                [profileId]: !p[profileId],
                            }))
                        }
                        data={tableHooks.items}
                    />
                </div>
            </Card>
            <Card
                className={classNames(
                    'flex flex-col p-0',
                    useNavV2 ? '!rounded-none !shadow-none' : 'bp4-card-rounded',
                )}
            >
                <ProfilesOverviewTable2
                    nested
                    store={store}
                    tableHooks={tableHooks2}
                    baseCurrency={baseCurrency}
                    showTableInBaseCurrency={showTableInBaseCurrency}
                    setBaseCurrency={setBaseCurrency}
                    setShowTableInBaseCurrency={setShowTableInBaseCurrency}
                    savedFiltersStore={savedFiltersStore}
                    tableClassName={
                        useNavV2 ? '[&>div]:!rounded-none [&>div]:!shadow-none' : undefined
                    }
                />
            </Card>
        </Layout>
    )
})

const defaultFilters = [...PerformanceFilters]

/** Has extra props for the currency and normalization switch */
type TableProps2 = TableProps<TableType> & {
    showTableInBaseCurrency: boolean
    setShowTableInBaseCurrency: (v: boolean) => void
    baseCurrency: string
    setBaseCurrency: (v: string) => void
    tableClassName?: string
}

export const ProfilesOverviewTable2 = observer(
    ({
        nested,
        store,
        savedFiltersStore,
        tableHooks,
        showTableInBaseCurrency,
        setShowTableInBaseCurrency,
        baseCurrency,
        setBaseCurrency,
        tableClassName,
    }: TableProps2): JSX.Element => {
        const useUserValues = useUser()
        const filters = useKdpFilters(defaultFilters)

        const { params } = tableHooks

        const breadcrumbs: BreadcrumbProps[] = [{ text: 'Manage' }]

        const cols3 = useMemo(() => {
            return newColumns(useUserValues.is_kdp, showTableInBaseCurrency, baseCurrency)
        }, [useUserValues.is_kdp, showTableInBaseCurrency, baseCurrency])

        return (
            <Layout
                breadcrumbs={breadcrumbs}
                primary="Ad Accounts Overview"
                nested={nested}
                params={params}
            >
                <AgGridTable
                    name="Profiles Overview"
                    columns={cols3}
                    tableHooks={tableHooks}
                    store={store}
                    showHeader={false}
                    savedFiltersStore={savedFiltersStore}
                    filters={filters}
                    search
                    searchPlaceholder="Search by profile"
                    columnSelection
                    nested={nested}
                    className={tableClassName}
                    context={{
                        baseCurrency,
                        showTableInBaseCurrency,
                    }}
                    otherActions={
                        <div className="flex items-center mr-2">
                            <Switch
                                className="mb-0 mr-2 whitespace-nowrap"
                                alignIndicator="right"
                                checked={showTableInBaseCurrency}
                                onChange={(e: any) => setShowTableInBaseCurrency(e.target.checked)}
                                label="Normalize currency"
                            />
                            <div className="flex items-center">
                                <label className="mr-2 mb-0 whitespace-nowrap">as</label>
                                <HTMLSelect
                                    options={[
                                        { label: 'USD', value: 'USD' },
                                        { label: 'EUR', value: 'EUR' },
                                        { label: 'GBP', value: 'GBP' },
                                        { label: 'MXN', value: 'MXN' },
                                        { label: 'CAD', value: 'CAD' },
                                        { label: 'BRL', value: 'BRL' },
                                        { label: 'JPY', value: 'JPY' },
                                        { label: 'INR', value: 'INR' },
                                        { label: 'AUD', value: 'AUD' },
                                    ]}
                                    value={baseCurrency}
                                    onChange={(e) => setBaseCurrency(e.target.value)}
                                    style={{ width: '70px' }}
                                />
                            </div>
                        </div>
                    }
                />
            </Layout>
        )
    },
)

const ProfileCellRenderer = ({
    data,
    context,
}: CellProps<Profile & ExpandedPerformanceMetrics>) => {
    // @ts-ignore
    const p = data.profile
    return (
        <div className={classNames('flex items-center')}>
            <div className={classNames('flex items-center')}>
                <ReactCountryFlag
                    /* @ts-ignore*/
                    title={p.country_code}
                    countryCode={p.country_code === 'UK' ? 'gb' : p.country_code}
                    svg
                    className="mr-2 text-lg"
                />
                <div className="flex flex-col">
                    <span className={classNames({ blur: context.userHook.screenshotMode })}>
                        {p.nickname || p.name}
                    </span>
                </div>
            </div>
        </div>
    )
}

const ProfileCell: AgGridColumnDef<Profile & ExpandedPerformanceMetrics> = {
    field: 'profile_id',
    headerName: 'Profile',
    headerComponent: DummyHeader,
    cellRenderer: ProfileCellRenderer,
}

const moneyColumns = {
    cost: true,
    cpc: true,
    rpc: true,
    cac: true,
    blended_cac: true,
    aov: true,
    estimated_royalties: true,
    blended_aov: true,
    blended_profit: true,
    blended_rpc: true,
    sales: true,
    adjusted_sales: true,
    adjusted_estimated_royalties: true,
}

const newColumns = <T,>(
    isKdp: boolean,
    showTableInBaseCurrency: boolean,
    baseCurrency: string,
): AgGridColumnDef<TableType>[] => {
    const cols = [
        ProfileCell,
        ...defaultPerfColumns.map((col) => {
            if (!moneyColumns[col.field as string]) {
                return col
            }

            return {
                ...col,
                valueFormatter: ({ value, data }) => {
                    if (showTableInBaseCurrency) {
                        return moneyWithCurrencyCode(value, baseCurrency)
                    }
                    return money(value, Marketplaces[data?.profile?.marketplace_id as Marketplace])
                },
                headerFormatter: ({ value }) => moneyWithCurrencyCode(value, baseCurrency),
            }
        }),
    ]

    // @ts-ignore
    addExtraCols(cols, isKdp)

    // @ts-ignore
    return cols.filter((c) => c.field != 'ideal_cpc' && c.field != 'target_acos')
}
